import React, { useState, useEffect } from 'react';
import { Plus, Users, Settings, Save, Calendar, MessageSquare, DollarSign, Clock, Package2, Wrench, Users2, BarChart, Settings2, ListChecks, Activity, FileBarChart2, FileText, BarChart3, ArrowLeft, Calculator, Megaphone, Receipt, MapPin, Building2, CalendarDays, Zap, ShoppingCart, Gift, Package, Folder, ChevronDown, Bell } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { getResourceRoles, updateResourceRole, NOTIFICATION_TYPES, type ResourceRole, type Permissions } from '../../lib/resourceRoles';
import { mergeHomeScreenAppOptions } from '../../lib/controlCentreHome';
import CreateRoleModal from '../../components/CreateRoleModal';
import { useNavigate } from 'react-router-dom';
import { useAppSystem } from '../../hooks/useAppSystem';
import { supabase } from '../../lib/supabase';

export default function ResourcePermissions() {
  const { selectedBusiness } = useBusiness();
  const { coreApps, installedAddons } = useAppSystem();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<ResourceRole[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const [originalPermissions, setOriginalPermissions] = useState<Permissions | null>(null);
  const [showPermissions, setShowPermissions] = useState(false); // Mobile permissions view state
  const [homeScreenApps, setHomeScreenApps] = useState<any[]>([]);
  const [homeScreenDefaultAppId, setHomeScreenDefaultAppId] = useState<string | null>(null);
  const [isHomeScreenLoading, setIsHomeScreenLoading] = useState(false);
  const [calendarDefaultView, setCalendarDefaultView] = useState<'month' | 'week' | 'day'>('month');
  const [isCalendarDefaultViewLoading, setIsCalendarDefaultViewLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Check if apps are installed
  const isCalendarAppInstalled = coreApps.some(app => app.app?.slug === 'calendar') || 
                                 installedAddons.some(app => app.app?.slug === 'calendar');
  const isMessagesAppInstalled = coreApps.some(app => app.app?.slug === 'messages') || 
                                installedAddons.some(app => app.app?.slug === 'messages');
  const isMarketingAppInstalled = coreApps.some(app => app.app?.slug === 'marketing') || 
                                  installedAddons.some(app => app.app?.slug === 'marketing');
  const isCrmAppInstalled = coreApps.some(app => app.app?.slug === 'crm') || 
                            installedAddons.some(app => app.app?.slug === 'crm');
  const isPayrollAppInstalled = coreApps.some(app => app.app?.slug === 'payroll') || 
                                installedAddons.some(app => app.app?.slug === 'payroll');
  const isSuppliesAppInstalled = coreApps.some(app => app.app?.slug === 'supplies') || 
                                 installedAddons.some(app => app.app?.slug === 'supplies');
  const isServicesAppInstalled = coreApps.some(app => app.app?.slug === 'services') || 
                                 installedAddons.some(app => app.app?.slug === 'services');
  const isResourcesAppInstalled = coreApps.some(app => app.app?.slug === 'teams') || 
                                  installedAddons.some(app => app.app?.slug === 'teams');
  const isTasksAppInstalled = coreApps.some(app => app.app?.slug === 'tasks') || 
                             installedAddons.some(app => app.app?.slug === 'tasks');
  const isBookingsAppInstalled = coreApps.some(app => app.app?.slug === 'bookings') || 
                                 installedAddons.some(app => app.app?.slug === 'bookings');
  const isProposalsAppInstalled = coreApps.some(app => app.app?.slug === 'proposals') || 
                                  installedAddons.some(app => app.app?.slug === 'proposals');
  const isCustomersAppInstalled = coreApps.some(app => app.app?.slug === 'customers') || 
                                  installedAddons.some(app => app.app?.slug === 'customers');
  const isKpiDashboardAppInstalled = coreApps.some(app => app.app?.slug === 'analytics') || 
                                     installedAddons.some(app => app.app?.slug === 'analytics');
  const isAutomationsAppInstalled = coreApps.some(app => app.app?.slug === 'automations') || 
                                    installedAddons.some(app => app.app?.slug === 'automations');
  const isInvoicesAppInstalled = coreApps.some(app => app.app?.slug === 'invoices') || 
                                 installedAddons.some(app => app.app?.slug === 'invoices');
  const isProductsAppInstalled = coreApps.some(app => app.app?.slug === 'products') || 
                                 installedAddons.some(app => app.app?.slug === 'products');
  const isGiftCardsAppInstalled = coreApps.some(app => app.app?.slug === 'gift-cards') || 
                                   installedAddons.some(app => app.app?.slug === 'gift-cards');
  const isInventoryAppInstalled = coreApps.some(app => app.app?.slug === 'inventory') || 
                                  installedAddons.some(app => app.app?.slug === 'inventory');
  const isProjectsAppInstalled = coreApps.some(app => app.app?.slug === 'projects') || 
                                 installedAddons.some(app => app.app?.slug === 'projects');
  const isExpensesAppInstalled = coreApps.some(app => app.app?.slug === 'expenses') || 
                                 installedAddons.some(app => app.app?.slug === 'expenses');
  const isQueAppInstalled = coreApps.some(app => app.app?.slug === 'que') || 
                           installedAddons.some(app => app.app?.slug === 'que');

  // Load roles from database
  const loadRoles = async () => {
    if (!selectedBusiness) return;

    try {
      setIsLoading(true);
      setError(null);
      const rolesData = await getResourceRoles(selectedBusiness.id);
      setRoles(rolesData);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  };

  const loadHomeScreenApps = async () => {
    if (!selectedBusiness) return;

    try {
      setIsHomeScreenLoading(true);
      const { data, error: appsError } = await supabase
        .from('business_apps_v2')
        .select(`
          id,
          app_id,
          is_home_screen,
          app:app_registry_v2(
            id,
            name,
            slug,
            route_prefix
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'active')
        .order('visible_app_grid', { ascending: true });

      if (appsError) {
        console.error('Error loading home screen apps:', appsError);
        return;
      }

      // Control Centre is a universal core system, not an installed app — always offer it.
      const merged = mergeHomeScreenAppOptions(data || []);
      setHomeScreenApps(merged);
      const defaultHomeApp = merged.find((app: any) => app.is_home_screen);
      setHomeScreenDefaultAppId(defaultHomeApp?.app_id || null);
    } catch (err) {
      console.error('Error loading home screen apps:', err);
    } finally {
      setIsHomeScreenLoading(false);
    }
  };

  const loadCalendarDefaultView = async () => {
    if (!selectedBusiness) return;

    try {
      setIsCalendarDefaultViewLoading(true);
      const { data, error } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'default_calendar_view',
          p_default_value: 'month'
        });

      if (!error && data) {
        setCalendarDefaultView((data as 'month' | 'week' | 'day') || 'month');
      }
    } catch (err) {
      console.error('Error loading calendar default view:', err);
    } finally {
      setIsCalendarDefaultViewLoading(false);
    }
  };

  // Load roles on component mount and when business changes
  useEffect(() => {
    loadRoles();
    loadHomeScreenApps();
    loadCalendarDefaultView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness]);

  const handleCreateRole = () => {
    setIsCreateModalOpen(true);
  };

  const handleRoleCreated = () => {
    // Reload roles after creating a new one
    loadRoles();
    // Reset selection and permissions state
    setSelectedRoleId(null);
    setPermissions(null);
    setOriginalPermissions(null);
    setShowPermissions(false);
  };

  const handleRoleSelect = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    if (role) {
      setSelectedRoleId(roleId);
      // Deep copy permissions to avoid reference issues
      const permissionsCopy = JSON.parse(JSON.stringify(role.permissions));
      permissionsCopy.calendar = {
        ...permissionsCopy.calendar,
        defaultView: permissionsCopy.calendar?.defaultView ?? null,
        monthView: permissionsCopy.calendar?.monthView ?? false,
        weekView: permissionsCopy.calendar?.weekView ?? false,
        dayView: permissionsCopy.calendar?.dayView ?? false,
      };
      permissionsCopy.topLevel = {
        ...permissionsCopy.topLevel,
        nexusConnect: permissionsCopy.topLevel?.nexusConnect ?? false,
      };
      permissionsCopy.projects = {
        pageAccess: permissionsCopy.projects?.pageAccess ?? false,
      };
      permissionsCopy.queApp = {
        pageAccess: permissionsCopy.queApp?.pageAccess ?? false,
        viewBuilder: permissionsCopy.queApp?.viewBuilder ?? false,
      };
      setPermissions(permissionsCopy);
      setOriginalPermissions(JSON.parse(JSON.stringify(permissionsCopy)));
      setShowPermissions(true); // Show permissions on mobile
      setError(null); // Clear any previous errors
    }
  };

  const handlePermissionChange = (categoryKey: keyof Permissions, permissionKey: string, value: boolean) => {
    if (!permissions) return;

    setPermissions(prev => {
      if (!prev) return prev;
      
      // Ensure the category exists, initialize with empty object if it doesn't
      const currentCategory = prev[categoryKey] || {} as any;
      
      return {
        ...prev,
        [categoryKey]: {
          ...currentCategory,
          [permissionKey]: value
        }
      };
    });
  };

  const handleTopLevelChange = (
    permissionKey: keyof Permissions['topLevel'],
    value: string | boolean | null
  ) => {
    if (!permissions) return;

    setPermissions(prev => {
      if (!prev) return prev;
      const currentTopLevel = prev.topLevel || {};

      return {
        ...prev,
        topLevel: {
          ...currentTopLevel,
          [permissionKey]: value
        }
      };
    });
  };

  const handleCalendarDefaultViewChange = (value: 'month' | 'week' | 'day' | null) => {
    if (!permissions) return;

    setPermissions(prev => {
      if (!prev) return prev;
      const currentCalendar = prev.calendar || ({} as Permissions['calendar']);

      return {
        ...prev,
        calendar: {
          ...currentCalendar,
          defaultView: value
        }
      };
    });
  };

  const handleSave = async () => {
    if (!selectedRoleId || !permissions) return;

    try {
      setIsSaving(true);
      setError(null);
      // Update the role and get the updated data back
      const updatedRole = await updateResourceRole(selectedRoleId, { permissions });
      // Update local state with the fresh data from database
      const permissionsCopy = JSON.parse(JSON.stringify(updatedRole.permissions));
      setPermissions(permissionsCopy);
      setOriginalPermissions(JSON.parse(JSON.stringify(permissionsCopy)));
      // Reload roles list to ensure it's in sync
      await loadRoles();
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError('Failed to save permissions. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = () => {
    return JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
  };

  const toggleCategoryExpanded = (categoryName: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  const handleBackToRoles = () => {
    setShowPermissions(false);
    setSelectedRoleId(null);
    setPermissions(null);
    setOriginalPermissions(null);
  };

  const handleBackToTeams = () => {
    navigate('/resources');
  };

  // Get permission categories filtered by installed apps
  const getPermissionCategories = (): Array<{
    name: string;
    displayName: string;
    icon: any;
    permissions: Array<{ key: string; label: string; group?: string; nestedCategory?: string }>;
    appSlug?: string; // Track which app this category belongs to
  }> => {
    const allCategories: Array<{
      name: string;
      displayName: string;
      icon: any;
      permissions: Array<{ key: string; label: string; group?: string }>;
      appSlug?: string;
    }> = [
      {
        name: 'topLevel',
        displayName: 'Top Level Permissions',
        icon: Settings2,
        permissions: []
      },
      {
        name: 'notifications',
        displayName: 'Push Notifications',
        icon: Bell,
        permissions: NOTIFICATION_TYPES.map(n => ({ key: n.key, label: n.label })),
      },
      {
        name: 'calendar',
        displayName: 'Calendar',
        icon: Calendar,
        appSlug: 'calendar', // Calendar app
        permissions: [
          { key: 'pageAccess', label: 'App Access' },
          // Main Page permissions
          { key: 'sales', label: 'View Sales Summary', group: 'mainPage' },
          { key: 'viewAllBookings', label: 'View All Bookings', group: 'mainPage' },
          { key: 'bookingInformation', label: 'View Booking Information', group: 'mainPage' },
          { key: 'monthView', label: 'Month View', group: 'mainPage' },
          { key: 'weekView', label: 'Week View', group: 'mainPage' },
          { key: 'dayView', label: 'Day View', group: 'mainPage' },
          { key: 'hideBookingList', label: 'Hide Booking List', group: 'mainPage' },
          { key: 'hideSearchBar', label: 'Hide Search Bar', group: 'mainPage' },
          { key: 'hideUnscheduledTab', label: 'Hide Unscheduled Tab', group: 'mainPage' },
          // Booking Details permissions
          { key: 'bookingSalesInfo', label: 'View Booking Sales Info', group: 'bookingDetails' },
          { key: 'editBookingDetails', label: 'Edit Booking Details', group: 'bookingDetails' },
          { key: 'bookingMaterials', label: 'View Booking Materials', group: 'bookingDetails' },
          { key: 'bookingRatebook', label: 'View Booking Ratebook', group: 'bookingDetails' },
          { key: 'bookingProfitLoss', label: 'View Booking Profit & Loss', group: 'bookingDetails' },
          { key: 'bookingLiabilityWaiver', label: 'View Booking Liability Waiver', group: 'bookingDetails' },
          { key: 'createBookings', label: 'Create Bookings', group: 'mainPage' }
        ]
      },
      {
        name: 'messages',
        displayName: 'Messages',
        icon: MessageSquare,
        appSlug: 'messages', // Messages app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'marketing',
        displayName: 'Marketing',
        icon: Megaphone,
        appSlug: 'marketing', // Marketing app
        permissions: [
          { key: 'pageAccess', label: 'App Access' },
          // Campaigns permissions (nested within marketing)
          { key: 'campaigns', label: 'Campaigns Access', group: 'campaigns', nestedCategory: 'campaigns' },
          // Sales Map permissions (nested within marketing)
          { key: 'salesMap', label: 'Sales Map Access', group: 'salesMap', nestedCategory: 'salesMap' }
        ]
      },
      {
        name: 'crm',
        displayName: 'CRM',
        icon: Building2,
        appSlug: 'crm', // CRM app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'payroll',
        displayName: 'Payroll',
        icon: Calculator,
        appSlug: 'payroll', // Payroll app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'supplies',
        displayName: 'Supplies',
        icon: Package2,
        appSlug: 'supplies', // Supplies app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'services',
        displayName: 'Services',
        icon: Wrench,
        appSlug: 'services', // Services app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'resources',
        displayName: 'Teams',
        icon: Users2,
        appSlug: 'teams', // Teams app (resources permission maps to teams app)
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'tasks',
        displayName: 'Docs',
        icon: ListChecks,
        appSlug: 'tasks', // Docs app (repurposed Tasks slug)
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'bookings',
        displayName: 'Bookings',
        icon: CalendarDays,
        appSlug: 'bookings', // Bookings app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'proposals',
        displayName: 'Proposals',
        icon: FileText,
        appSlug: 'proposals', // Proposals app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'customers',
        displayName: 'Customers',
        icon: Users,
        appSlug: 'customers', // Customers app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'kpiDashboard',
        displayName: 'Analytics',
        icon: BarChart3,
        appSlug: 'analytics', // Analytics app (slug is 'analytics')
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'automations',
        displayName: 'Automate',
        icon: Zap,
        appSlug: 'automations', // Automations app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'invoices',
        displayName: 'Invoices',
        icon: Receipt,
        appSlug: 'invoices', // Invoices app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'products',
        displayName: 'Store',
        icon: ShoppingCart,
        appSlug: 'products', // Products app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'giftCards',
        displayName: 'Gift Cards',
        icon: Gift,
        appSlug: 'gift-cards', // Gift Cards app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'inventory',
        displayName: 'Inventory',
        icon: Package,
        appSlug: 'inventory', // Inventory app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'projects',
        displayName: 'Projects',
        icon: Folder,
        appSlug: 'projects', // Projects app
        permissions: [
          { key: 'pageAccess', label: 'App Access' }
        ]
      },
      {
        name: 'expenses',
        displayName: 'Expenses',
        icon: Receipt,
        appSlug: 'expenses',
        permissions: [
          { key: 'pageAccess', label: 'App Access' },
          { key: 'appVisibility', label: 'App Visibility' },
          { key: 'uploadExpense', label: 'Upload Expense' }
        ]
      },
      {
        name: 'queApp',
        displayName: 'Que',
        icon: Activity,
        appSlug: 'que', // Que app
        permissions: [
          { key: 'pageAccess', label: 'App Access' },
          { key: 'viewBuilder', label: 'View Builder' }
        ]
      }
    ];

    // Filter categories based on installed apps
    return allCategories.filter(category => {
      // Calendar: only show if calendar app is installed
      if (category.appSlug === 'calendar') {
        return isCalendarAppInstalled;
      }
      // Messages: only show if messages app is installed
      if (category.appSlug === 'messages') {
        return isMessagesAppInstalled;
      }
      // Marketing: only show if marketing app is installed
      if (category.appSlug === 'marketing') {
        return isMarketingAppInstalled;
      }
      // CRM: only show if CRM app is installed
      if (category.appSlug === 'crm') {
        return isCrmAppInstalled;
      }
      // Payroll: only show if payroll app is installed
      if (category.appSlug === 'payroll') {
        return isPayrollAppInstalled;
      }
      // Supplies: only show if supplies app is installed
      if (category.appSlug === 'supplies') {
        return isSuppliesAppInstalled;
      }
      // Services: only show if services app is installed
      if (category.appSlug === 'services') {
        return isServicesAppInstalled;
      }
      // Resources/Teams: only show if teams app is installed
      if (category.appSlug === 'teams') {
        return isResourcesAppInstalled;
      }
      // Tasks: only show if tasks app is installed
      if (category.appSlug === 'tasks') {
        return isTasksAppInstalled;
      }
      // Bookings: only show if bookings app is installed
      if (category.appSlug === 'bookings') {
        return isBookingsAppInstalled;
      }
      // Proposals: only show if proposals app is installed
      if (category.appSlug === 'proposals') {
        return isProposalsAppInstalled;
      }
      // Customers: only show if customers app is installed
      if (category.appSlug === 'customers') {
        return isCustomersAppInstalled;
      }
      // KPI Dashboard: only show if analytics app is installed
      if (category.appSlug === 'analytics') {
        return isKpiDashboardAppInstalled;
      }
      // Automations: only show if automations app is installed
      if (category.appSlug === 'automations') {
        return isAutomationsAppInstalled;
      }
      // Invoices: only show if invoices app is installed
      if (category.appSlug === 'invoices') {
        return isInvoicesAppInstalled;
      }
      // Products: only show if products app is installed
      if (category.appSlug === 'products') {
        return isProductsAppInstalled;
      }
      // Gift Cards: only show if gift-cards app is installed
      if (category.appSlug === 'gift-cards') {
        return isGiftCardsAppInstalled;
      }
      // Inventory: only show if inventory app is installed
      if (category.appSlug === 'inventory') {
        return isInventoryAppInstalled;
      }
      // Projects: only show if projects app is installed
      if (category.appSlug === 'projects') {
        return isProjectsAppInstalled;
      }
      // Expenses: only show if expenses app is installed
      if (category.appSlug === 'expenses') {
        return isExpensesAppInstalled;
      }
      // Que: only show if que app is installed
      if (category.appSlug === 'que') {
        return isQueAppInstalled;
      }
      // All other categories show by default (will be updated step by step)
      return true;
    });
  };

  const permissionCategories = getPermissionCategories();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <button
                onClick={handleBackToTeams}
                className="flex items-center shrink-0 text-gray-600 hover:text-gray-900 transition-colors p-1.5 -ml-1.5 rounded-xl active:bg-gray-100 touch-manipulation"
              >
                <ArrowLeft className="h-5 w-5" />
                <span className="hidden sm:inline ml-1.5 text-sm">Back to Teams</span>
              </button>
              <div className="hidden sm:block h-6 w-px bg-gray-200 shrink-0"></div>
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Permission Roles</h1>
            </div>
            <button
              onClick={handleCreateRole}
              className="inline-flex items-center shrink-0 px-3 sm:px-4 py-2 text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-colors touch-manipulation"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Create Role</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {error && (
          <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Roles List */}
          <div className={`lg:col-span-1 ${showPermissions ? 'hidden lg:block' : ''}`}>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-gray-100">
                <h2 className="text-[15px] sm:text-lg font-semibold text-gray-900">Roles</h2>
              </div>
              <div className="p-3 sm:p-6">
                {roles.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No roles created yet</p>
                    <button
                      onClick={handleCreateRole}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Create your first role
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {roles.map((role) => (
                      <button
                        key={role.id}
                        onClick={() => handleRoleSelect(role.id)}
                        className={`w-full text-left px-3.5 py-3 rounded-xl border transition-all active:scale-[0.98] touch-manipulation ${
                          selectedRoleId === role.id
                            ? 'border-blue-200 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100'
                        }`}
                      >
                        <div className="text-[15px] font-medium text-gray-900">{role.name}</div>
                        {role.description && (
                          <div className="text-xs text-gray-500 mt-0.5">{role.description}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Permissions Panel */}
          <div className={`lg:col-span-2 ${!showPermissions ? 'hidden lg:block' : ''}`}>
            {selectedRoleId && permissions ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-3.5 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                    <button
                      onClick={handleBackToRoles}
                      className="lg:hidden flex items-center shrink-0 text-gray-600 hover:text-gray-900 p-1.5 -ml-1.5 rounded-xl active:bg-gray-100 touch-manipulation"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <h2 className="text-[15px] sm:text-lg font-semibold text-gray-900 truncate">
                      {roles.find(r => r.id === selectedRoleId)?.name} <span className="hidden sm:inline">Permissions</span>
                    </h2>
                  </div>
                  {hasChanges() && (
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="inline-flex items-center shrink-0 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 rounded-xl transition-colors touch-manipulation"
                    >
                      {isSaving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white sm:mr-2"></div>
                          <span className="hidden sm:inline">Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 sm:mr-2" />
                          <span className="hidden sm:inline">Save Changes</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <div className="p-3 sm:p-5 space-y-2.5 sm:space-y-3">
                  {permissionCategories.map((category) => {
                    const CategoryIcon = category.icon;
                    const categoryKey = category.name as keyof Permissions;
                    const categoryPermissions = permissions?.[categoryKey] as any;
                    const isTopLevel = category.name === 'topLevel';
                    const isCalendar = category.appSlug === 'calendar';
                    const isMarketing = category.appSlug === 'marketing';
                    const isDocs = category.appSlug === 'tasks';
                    const appAccessEnabled = categoryPermissions?.pageAccess === true;
                    const isExpanded = expandedCategories.has(category.name);

                    // Check if category has advanced permissions beyond pageAccess
                    const advancedPerms = category.permissions.filter(p => p.key !== 'pageAccess');
                    const hasAdvanced = advancedPerms.length > 0;

                    {/* ─── Top Level: special card with its own permissions ─── */}
                    if (isTopLevel) {
                      const defaultHomeApp = homeScreenApps.find(app => app.app_id === homeScreenDefaultAppId);
                      const defaultHomeLabel = defaultHomeApp?.app?.name || 'Not set';
                      const roleHomeScreenAppId = permissions?.topLevel?.homeScreenAppId ?? null;

                      return (
                        <div key={category.name} className="border border-gray-200 rounded-2xl bg-white overflow-hidden">
                          <div className="px-4 py-3.5 flex items-center gap-3 bg-gray-50/60 border-b border-gray-100">
                            <div className="w-9 h-9 bg-gradient-to-br from-gray-700 to-gray-600 rounded-[10px] flex items-center justify-center shadow-sm">
                              <Settings2 className="h-[18px] w-[18px] text-white" />
                            </div>
                            <span className="text-[15px] font-semibold text-gray-900">{category.displayName}</span>
                          </div>
                          <div className="p-4 space-y-3">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={Boolean(permissions?.topLevel?.canViewAppSettings)}
                                onChange={(e) => handleTopLevelChange('canViewAppSettings', e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                              />
                              <span className="ml-3 text-sm text-gray-700">Can View App Settings</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={Boolean(permissions?.topLevel?.nexusConnect)}
                                onChange={(e) => handleTopLevelChange('nexusConnect', e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                              />
                              <span className="ml-3 text-sm text-gray-700">Nexus Connect</span>
                            </label>
                            <div className="pt-1">
                              <div className="text-xs text-gray-500 mb-2">
                                Business default: <span className="font-medium text-gray-700">{defaultHomeLabel}</span>
                              </div>
                              <label className="block text-sm font-medium text-gray-700 mb-1.5">Home Screen</label>
                              <select
                                value={roleHomeScreenAppId || ''}
                                onChange={(e) => handleTopLevelChange('homeScreenAppId', e.target.value || null)}
                                disabled={isHomeScreenLoading || homeScreenApps.length === 0}
                                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                              >
                                <option value="">Use business default ({defaultHomeLabel})</option>
                                {homeScreenApps.map((app) => (
                                  <option key={app.id} value={app.app_id}>{app.app?.name || 'Unnamed App'}</option>
                                ))}
                              </select>
                              <p className="mt-1.5 text-xs text-gray-400">Overrides the business home screen for this role.</p>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    {/* ─── Push Notifications: dedicated card, all toggles always visible ─── */}
                    if (category.name === 'notifications') {
                      return (
                        <div key={category.name} className="border border-gray-200 rounded-2xl bg-white overflow-hidden">
                          <div className="px-4 py-3.5 flex items-center gap-3 bg-gray-50/60 border-b border-gray-100">
                            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-500 rounded-[10px] flex items-center justify-center shadow-sm">
                              <Bell className="h-[18px] w-[18px] text-white" />
                            </div>
                            <span className="text-[15px] font-semibold text-gray-900">{category.displayName}</span>
                          </div>
                          <div className="px-4 pt-2.5 pb-1">
                            <p className="text-xs text-gray-400">Choose which push notifications resources with this role receive. Admins always receive all notifications.</p>
                          </div>
                          <div className="p-4 pt-2 space-y-3">
                            {category.permissions.map((perm) => {
                              const enabled = categoryPermissions?.[perm.key] === true;
                              return (
                                <div key={perm.key} className="flex items-center justify-between gap-3">
                                  <span className="text-[14px] text-gray-700">{perm.label}</span>
                                  <button
                                    role="switch"
                                    aria-checked={enabled}
                                    onClick={() => handlePermissionChange(categoryKey, perm.key, !enabled)}
                                    className={`relative inline-flex h-[31px] w-[51px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none ${
                                      enabled ? 'bg-blue-600' : 'bg-gray-200'
                                    }`}
                                  >
                                    <span className={`pointer-events-none inline-block h-[27px] w-[27px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                                      enabled ? 'translate-x-5' : 'translate-x-0'
                                    }`} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }

                    {/* ─── App category tile ─── */}
                    return (
                      <div key={category.name} className="border border-gray-200 rounded-2xl bg-white overflow-hidden">
                        {/* App tile row: icon + name + iOS toggle */}
                        <div className="px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-500 rounded-[10px] flex items-center justify-center shadow-sm">
                              <CategoryIcon className="h-[18px] w-[18px] text-white" />
                            </div>
                            <span className="text-[15px] font-medium text-gray-900">{category.displayName}</span>
                          </div>
                          {/* iOS-style toggle */}
                          <button
                            role="switch"
                            aria-checked={appAccessEnabled}
                            onClick={() => handlePermissionChange(categoryKey, 'pageAccess', !appAccessEnabled)}
                            className={`relative inline-flex h-[31px] w-[51px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none ${
                              appAccessEnabled ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                          >
                            <span className={`pointer-events-none inline-block h-[27px] w-[27px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                              appAccessEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>

                        {/* Docs: "Can complete docs" — always shown, independent of app access */}
                        {isDocs && (
                          <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <span className="text-[14px] font-medium text-gray-900">Can complete docs</span>
                              <p className="text-xs text-gray-500 mt-0.5">Show the “Complete Document” quick-add option. Works even without app access.</p>
                            </div>
                            <button
                              role="switch"
                              aria-checked={categoryPermissions?.canComplete !== false}
                              onClick={() => handlePermissionChange(categoryKey, 'canComplete', categoryPermissions?.canComplete === false)}
                              className={`relative inline-flex h-[31px] w-[51px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline-none ${
                                categoryPermissions?.canComplete !== false ? 'bg-blue-600' : 'bg-gray-200'
                              }`}
                            >
                              <span className={`pointer-events-none inline-block h-[27px] w-[27px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                                categoryPermissions?.canComplete !== false ? 'translate-x-5' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                        )}

                        {/* Advanced Permissions – only visible when app access is ON and extra perms exist */}
                        {appAccessEnabled && hasAdvanced && (
                          <div className="border-t border-gray-100">
                            <button
                              onClick={() => toggleCategoryExpanded(category.name)}
                              className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                            >
                              <span className="font-medium">Advanced Permissions</span>
                              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                            </button>

                            {isExpanded && (
                              <div className="px-4 pb-4 space-y-3">
                                {(() => {
                                  {/* ── Calendar: grouped sections ── */}
                                  if (isCalendar) {
                                    const mainPagePermissions = category.permissions.filter(p => p.group === 'mainPage');
                                    const bookingDetailsPermissions = category.permissions.filter(p => p.group === 'bookingDetails');

                                    return (
                                      <>
                                        <div className="border border-gray-200 rounded-xl p-3.5 bg-gray-50/60">
                                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Main Page</h4>
                                          <div className="space-y-2.5">
                                            {mainPagePermissions.map((perm) => (
                                              <label key={perm.key} className="flex items-center cursor-pointer">
                                                <input
                                                  type="checkbox"
                                                  checked={Boolean(categoryPermissions?.[perm.key])}
                                                  onChange={(e) => handlePermissionChange(categoryKey, perm.key, e.target.checked)}
                                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">{perm.label}</span>
                                              </label>
                                            ))}
                                            <div className="pt-2">
                                              {(() => {
                                                const defaultViewLabel = calendarDefaultView === 'month' ? 'Month' : calendarDefaultView === 'week' ? 'Week' : 'Day';
                                                return (
                                                  <>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Default View</label>
                                                    <select
                                                      value={categoryPermissions?.defaultView || ''}
                                                      onChange={(e) => handleCalendarDefaultViewChange((e.target.value as 'month' | 'week' | 'day' | '') || null)}
                                                      disabled={isCalendarDefaultViewLoading}
                                                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                                    >
                                                      <option value="">Use business default ({defaultViewLabel})</option>
                                                      <option value="month">Month</option>
                                                      <option value="week">Week</option>
                                                      <option value="day">Day</option>
                                                    </select>
                                                    <p className="mt-1.5 text-xs text-gray-400">Sets the default calendar view for this role.</p>
                                                  </>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="border border-gray-200 rounded-xl p-3.5 bg-gray-50/60">
                                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Booking Details</h4>
                                          <div className="space-y-2.5">
                                            {bookingDetailsPermissions.map((perm) => (
                                              <label key={perm.key} className="flex items-center cursor-pointer">
                                                <input
                                                  type="checkbox"
                                                  checked={Boolean(categoryPermissions?.[perm.key])}
                                                  onChange={(e) => handlePermissionChange(categoryKey, perm.key, e.target.checked)}
                                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                                />
                                                <span className="ml-3 text-sm text-gray-700">{perm.label}</span>
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                      </>
                                    );
                                  }

                                  {/* ── Marketing: nested categories ── */}
                                  if (isMarketing) {
                                    const campaignsPermission = category.permissions.find(p => p.group === 'campaigns');
                                    const salesMapPermission = category.permissions.find(p => p.group === 'salesMap');
                                    const campaignsPermissions = permissions?.campaigns as any;
                                    const salesMapPermissions = permissions?.salesMap as any;

                                    return (
                                      <>
                                        <div className="border border-gray-200 rounded-xl p-3.5 bg-gray-50/60">
                                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Campaigns</h4>
                                          {campaignsPermission && (
                                            <label className="flex items-center cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={Boolean(campaignsPermissions?.pageAccess)}
                                                onChange={(e) => handlePermissionChange('campaigns' as keyof Permissions, 'pageAccess', e.target.checked)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                              />
                                              <span className="ml-3 text-sm text-gray-700">{campaignsPermission.label}</span>
                                            </label>
                                          )}
                                        </div>
                                        <div className="border border-gray-200 rounded-xl p-3.5 bg-gray-50/60">
                                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2.5">Sales Map</h4>
                                          {salesMapPermission && (
                                            <label className="flex items-center cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={Boolean(salesMapPermissions?.pageAccess)}
                                                onChange={(e) => handlePermissionChange('salesMap' as keyof Permissions, 'pageAccess', e.target.checked)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                              />
                                              <span className="ml-3 text-sm text-gray-700">{salesMapPermission.label}</span>
                                            </label>
                                          )}
                                        </div>
                                      </>
                                    );
                                  }

                                  {/* ── Default: simple checkbox list for extra permissions ── */}
                                  return advancedPerms.map((perm) => (
                                    <label key={perm.key} className="flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(categoryPermissions?.[perm.key])}
                                        onChange={(e) => handlePermissionChange(categoryKey, perm.key, e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                      />
                                      <span className="ml-3 text-sm text-gray-700">{perm.label}</span>
                                    </label>
                                  ));
                                })()}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-8 sm:p-12 text-center">
                  <Settings className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1">Select a Role</h3>
                  <p className="text-sm text-gray-500">Choose a role from the list to configure its permissions</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Role Modal */}
      <CreateRoleModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onRoleCreated={handleRoleCreated}
        businessId={selectedBusiness?.id || ''}
      />
    </div>
  );
}



